import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchBryqStatus, sendBryq, type BryqBoardView, type BryqEntry, type BryqKey } from '../../services/bryqApi';
import { BryqModal } from './BryqModal';

/**
 * Bryq for the whole board (Oct 2026) — the same shape as NotesContext.
 *
 * The 🧠 Bryq chip sits next to 📝 on every card in every tab. Rather than thread boardEmail and
 * modal state through each tab, the provider owns one picker and the board's Bryq picture:
 *  - `openBryq` — the chip: send Bryq as a FILTER step (decide on the next interview after the result),
 *  - `send` — an invite dialog's "Send Bryq in parallel to this interview",
 *  - `statusFor` / `view` — read once per board (`/bryq-status`, separate from the board build so
 *    the board does not wait on it): the chip colour and the Overdue tab's Bryq section.
 */
interface BryqTarget {
  opportunityId: string;
  candidateName: string;
}

interface BryqApi {
  openBryq: (target: BryqTarget) => void;
  send: (opportunityId: string, test: BryqKey) => Promise<void>;
  statusFor: (opportunityId: string) => BryqEntry[];
  view: BryqBoardView | null;
}

const BryqCtx = createContext<BryqApi | null>(null);

/** Null outside a provider — callers render nothing then, as NotesChip does. */
export function useBryq(): BryqApi | null {
  return useContext(BryqCtx);
}

const NONE: BryqEntry[] = [];

export function BryqProvider({ boardEmail, children }: { boardEmail: string; children: ReactNode }) {
  const [target, setTarget] = useState<BryqTarget | null>(null);
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ['bryq-status', boardEmail],
    queryFn: () => fetchBryqStatus(boardEmail),
    // A failed read only means grey chips and no Bryq section — never a broken board.
    retry: false,
    staleTime: 60_000,
  });
  const view = status.data ?? null;

  const openBryq = useCallback((t: BryqTarget) => setTarget(t), []);
  const close = useCallback(() => setTarget(null), []);
  const send = useCallback(
    async (opportunityId: string, test: BryqKey, source: 'alone' | 'with-invite' = 'with-invite') => {
      await sendBryq({ opportunityId, boardEmail, test, source });
      void queryClient.invalidateQueries({ queryKey: ['bryq-status', boardEmail] });
    },
    [boardEmail, queryClient]
  );
  const statusFor = useCallback((id: string) => view?.byOpportunity?.[id] ?? NONE, [view]);
  const api = useMemo(() => ({ openBryq, send, statusFor, view }), [openBryq, send, statusFor, view]);

  return (
    <BryqCtx.Provider value={api}>
      {children}
      {target && (
        <BryqModal
          candidateName={target.candidateName}
          opportunityId={target.opportunityId}
          onClose={close}
          onSend={(test) => send(target.opportunityId, test, 'alone')}
        />
      )}
    </BryqCtx.Provider>
  );
}
